import { useTheme } from '@mui/material';
import { EntityStatus } from '@verifime/utils';

export type TIconName =
  | 'checkCircle'
  | 'block'
  | 'remove'
  | 'pending'
  | 'closeFullscreenRounded'
  | 'checkCircleRounded'
  | 'warningRounded'
  | 'cancelRounded'
  | 'blockRounded'
  | 'radioButtonUncheckedRounded'
  | 'removeCircleRounded'
  | 'errorRounded'
  | 'keyboardArrowDownRounded'
  | 'deleteRounded'
  | 'warningAmberRounded'
  | 'addRounded'
  | 'closeRounded'
  | 'editRounded'
  | 'verifiedRounded'
  | 'arrowRightRounded'
  | 'arrowLeftRounded'
  | 'LockRounded'
  | 'emailRounded'
  | 'moreVertRounded'
  | 'autoAwesomeRounded'
  | 'reviewsRounded'
  | 'fileCopyRounded'
  | 'uploadFileIcon'
  | 'imageNotSupportedRounded'
  | 'openInNewRounded'
  | 'pdfRounded'
  | 'replayRounded'
  | 'infoRounded'
  | 'fileDownloadRounded'
  | 'checkRounded';

/**
 * Semantic color object from theme palette (status, risk, entity, interactive)
 *
 * For usage examples, see ColoredIcon and ColoredChip components.
 * These components automatically handle color logic via the `statusColor` prop.
 */
export type StatusColor = {
  /** Text color - contrasts with fill background */
  text: string;
  /** Main background color (e.g., chip background, icon main color) */
  fill?: string;
  /** Border color for outlined variants */
  border?: string;
  /** Contrasting color for icons inside colored backgrounds */
  icon?: string;
};

/**
 * Hook to get generic status colors from the theme
 * @param status - The status type: 'success' | 'warning' | 'error' | 'info'
 */
export function useStatusColor(status: 'success' | 'warning' | 'error' | 'info'): StatusColor {
  const theme = useTheme();
  return theme.palette.status[status];
}

/**
 * Hook to get interactive state colors from the theme
 * @param state - The interactive state: 'disabled' | 'enabled'
 */
export function useInteractiveColor(state: 'disabled' | 'enabled'): StatusColor {
  const theme = useTheme();
  return theme.palette.interactive[state];
}

/**
 * Hook to get risk level colors from the theme
 * @param risk - The risk level type
 */
export function useRiskColor(risk: 'Extreme' | 'High' | 'Medium' | 'Low' | 'Pending'): StatusColor {
  const theme = useTheme();
  const riskMap: Record<string, keyof typeof theme.palette.risk> = {
    Extreme: 'extreme',
    High: 'high',
    Medium: 'medium',
    Low: 'low',
    Pending: 'pending',
  };
  return theme.palette.risk[riskMap[risk]];
}

/**
 * Hook to get entity status colors from the theme
 * @param entityStatus - The entity status enum value
 */
export function useEntityColor(entityStatus: EntityStatus): StatusColor {
  const theme = useTheme();
  const entityMap: Record<EntityStatus, keyof typeof theme.palette.entity> = {
    [EntityStatus.Verified]: 'verified',
    [EntityStatus.Rejected]: 'rejected',
    [EntityStatus.Draft]: 'draft',
    [EntityStatus.InProgress]: 'inProgress',
    [EntityStatus.Empty]: 'empty',
  };
  return theme.palette.entity[entityMap[entityStatus]];
}

/**
 * Generic hook to get any semantic color from theme
 * Provides a single, simple interface that hides internal structure
 */
export function useSemanticColor(
  semantic:
    | { category: 'status'; value: 'success' | 'warning' | 'error' | 'info' }
    | { category: 'interactive'; value: 'disabled' | 'enabled' }
    | { category: 'risk'; value: 'Extreme' | 'High' | 'Medium' | 'Low' | 'Pending' }
    | { category: 'entity'; value: EntityStatus },
): StatusColor {
  const theme = useTheme();

  switch (semantic.category) {
    case 'status':
      return theme.palette.status[semantic.value];
    case 'interactive':
      return theme.palette.interactive[semantic.value];
    case 'risk': {
      const riskMap: Record<string, keyof typeof theme.palette.risk> = {
        Extreme: 'extreme',
        High: 'high',
        Medium: 'medium',
        Low: 'low',
        Pending: 'pending',
      };
      return theme.palette.risk[riskMap[semantic.value]];
    }
    case 'entity': {
      const entityMap: Record<EntityStatus, keyof typeof theme.palette.entity> = {
        [EntityStatus.Verified]: 'verified',
        [EntityStatus.Rejected]: 'rejected',
        [EntityStatus.Draft]: 'draft',
        [EntityStatus.InProgress]: 'inProgress',
        [EntityStatus.Empty]: 'empty',
      };
      return theme.palette.entity[entityMap[semantic.value]];
    }
  }
}
